/* todomamás · theme.js
   Carrito lateral (Section Rendering API), formularios de producto,
   menú de escritorio y menú móvil. Sin dependencias. */
(() => {
  const theme = window.theme || {};
  const CART_SECTION = 'cart-drawer';
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /* ---------- Utilidades ---------- */
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  const postJSON = async (url, body) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await response.json();
    if (!response.ok || data.status) {
      const error = new Error(data.description || data.message || theme.strings?.cartError);
      error.data = data;
      throw error;
    }
    return data;
  };

  /* ---------- Contador de la cabecera ---------- */
  const updateCount = (count) => {
    document.querySelectorAll('[data-carrito-contador]').forEach((badge) => {
      const visible = badge.querySelector('[aria-hidden]');
      if (visible) visible.textContent = count;
      badge.hidden = Number(count) === 0;
    });
  };

  /* ---------- Carrito lateral ---------- */
  class CartDrawer {
    constructor(root) {
      this.root = root;
      this.panel = root.querySelector('.carrito-lateral__panel');
      this.lastTrigger = null;
      this.onKeydown = this.onKeydown.bind(this);

      root.addEventListener('click', (event) => {
        if (event.target.closest('[data-carrito-cerrar]')) {
          this.close();
          return;
        }
        const change = event.target.closest('[data-carrito-cambiar]');
        if (change) {
          this.change(change.dataset.linea, change.dataset.cantidad);
        }
      });

      root.addEventListener('change', (event) => {
        const input = event.target.closest('[data-carrito-input]');
        if (!input) return;
        const quantity = Math.max(0, parseInt(input.value, 10) || 0);
        this.change(input.dataset.linea, quantity);
      });
    }

    get content() {
      return this.root.querySelector('#CarritoLateral-contenido');
    }

    open(trigger) {
      this.lastTrigger = trigger || document.activeElement;
      this.root.removeAttribute('inert');
      this.root.classList.add('is-abierto');
      document.body.classList.add('bloqueo-scroll');
      document.addEventListener('keydown', this.onKeydown);
      requestAnimationFrame(() => this.panel.focus());
      this.loadRecommendations();
    }

    close() {
      this.root.classList.remove('is-abierto');
      this.root.setAttribute('inert', '');
      document.body.classList.remove('bloqueo-scroll');
      document.removeEventListener('keydown', this.onKeydown);
      if (this.lastTrigger && document.contains(this.lastTrigger)) this.lastTrigger.focus();
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = [...this.panel.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    render(sections) {
      const html = sections && sections[CART_SECTION];
      if (!html) return;
      const fresh = parseHTML(html).querySelector('#CarritoLateral-contenido');
      if (!fresh) return;
      this.content.querySelectorAll('[data-resenas]').forEach((c) => clearInterval(c.__intervalo));
      /* La barra de envío gratis crece desde donde estaba, no desde cero. */
      const antes = this.content.querySelector('[data-envio-gratis]')?.dataset.progreso;
      const nuevaBarra = fresh.querySelector('[data-envio-gratis]');
      if (nuevaBarra && antes !== undefined) {
        nuevaBarra.style.setProperty('--progreso-antes', `${antes}%`);
        if (Number(antes) < 100 && Number(nuevaBarra.dataset.progreso) >= 100) nuevaBarra.classList.add('is-celebrando');
      }
      this.content.replaceWith(fresh);
      updateCount(fresh.dataset.contador);
      fresh.querySelectorAll('[data-resenas]').forEach(rotarResenas);
      if (this.root.classList.contains('is-abierto')) this.loadRecommendations();
    }

    showError(message) {
      const box = this.root.querySelector('[data-carrito-error]');
      if (!box) return;
      box.textContent = message || theme.strings?.cartError;
      box.hidden = false;
    }

    async change(line, quantity) {
      this.root.classList.add('is-cargando');
      try {
        const data = await postJSON(`${theme.routes.cartChange}.js`, {
          line: Number(line),
          quantity: Number(quantity),
          sections: [CART_SECTION],
          sections_url: window.location.pathname
        });
        this.render(data.sections);
        if (Number(quantity) === 0) document.dispatchEvent(new CustomEvent('carrito:actualizado'));
      } catch (error) {
        this.showError(error.message);
      } finally {
        this.root.classList.remove('is-cargando');
      }
    }

    async loadRecommendations() {
      if (!theme.showRecommendations) return;
      const slot = this.root.querySelector('[data-recomendaciones]');
      if (!slot || !slot.dataset.productosIds) return;
      const clave = slot.dataset.clave;
      this.cacheRecos = this.cacheRecos || new Map();

      /* Ya calculadas para este mismo carrito: se vuelven a poner al momento
         (antes desaparecían al cambiar una cantidad). */
      if (this.cacheRecos.has(clave)) {
        const guardado = this.cacheRecos.get(clave);
        if (guardado) slot.replaceChildren(parseHTML(guardado).querySelector('[data-recos-total]'));
        return;
      }
      if (this.recosPendiente === clave) return;
      this.recosPendiente = clave;

      const pedir = async (productId, intent) => {
        const url = `${theme.routes.recommendations}?section_id=cart-recommendations&product_id=${productId}&limit=10&intent=${intent}`;
        const response = await fetch(url);
        if (!response.ok) return null;
        const doc = parseHTML(await response.text());
        const block = doc.querySelector('[data-recos-total]');
        return block && Number(block.dataset.recosTotal) > 0 ? block : null;
      };

      try {
        /* Cada producto del carrito, del último añadido al primero:
           primero sus complementarios y, si no hay, sus relacionados. */
        let block = null;
        for (const productId of slot.dataset.productosIds.split(',').filter(Boolean)) {
          block = (await pedir(productId, 'complementary')) || (await pedir(productId, 'related'));
          if (block) break;
        }
        this.cacheRecos.set(clave, block ? block.outerHTML : null);
        const currentSlot = this.root.querySelector('[data-recomendaciones]');
        if (block && currentSlot && currentSlot.dataset.clave === clave) currentSlot.replaceChildren(block);
      } catch (error) {
        /* Las recomendaciones son opcionales: si fallan, el carrito sigue funcionando. */
      } finally {
        if (this.recosPendiente === clave) this.recosPendiente = null;
      }
    }
  }

  const drawerRoot = document.querySelector('[data-carrito-lateral]');
  const drawer = drawerRoot && theme.cartType === 'drawer' ? new CartDrawer(drawerRoot) : null;

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-carrito-abrir]');
    if (!trigger || !drawer) return;
    event.preventDefault();
    drawer.open(trigger);
  });

  /* ---------- Formularios de producto (añadir al carrito) ---------- */
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-formulario-producto]');
    if (!form || !drawer) return;
    event.preventDefault();

    const button = form.querySelector('[type="submit"]');
    const errorBox = form.querySelector('[data-formulario-error]');
    if (button?.getAttribute('aria-disabled') === 'true') return;

    const formData = new FormData(form);
    const item = { id: Number(formData.get('id')), quantity: Number(formData.get('quantity') || 1) };
    const items = [item];

    if (errorBox) errorBox.hidden = true;
    button?.setAttribute('aria-busy', 'true');

    try {
      /* Regalo de la ficha (ebook): va con la compra, una sola vez por carrito. */
      const regaloId = Number(document.querySelector('[data-regalo]')?.dataset.regaloId || 0);
      if (regaloId && regaloId !== item.id) {
        try {
          const carrito = await fetch(`${theme.routes.cart}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
          if (!carrito.items.some((linea) => linea.variant_id === regaloId)) items.push({ id: regaloId, quantity: 1 });
        } catch (e) {
          items.push({ id: regaloId, quantity: 1 });
        }
      }
      const data = await postJSON(`${theme.routes.cartAdd}.js`, {
        items,
        sections: [CART_SECTION],
        sections_url: window.location.pathname
      });
      drawer.render(data.sections);
      drawer.open(button);
    } catch (error) {
      if (errorBox) {
        errorBox.textContent = error.message;
        errorBox.hidden = false;
      } else {
        drawer.showError(error.message);
      }
    } finally {
      button?.removeAttribute('aria-busy');
    }
  });

  /* ---------- Casilla de envío Express + Protección (carrito) ----------
     Desmarcada por defecto. Marcarla añade el producto «Express» al carrito;
     desmarcarla lo quita. Si en el carrito no queda nada más, se quita solo. */
  document.addEventListener('change', async (event) => {
    const check = event.target.closest('[data-express-check]');
    if (!check || !drawer) return;
    const caja = check.closest('[data-express]');
    const variante = caja?.dataset.expressVariante;
    if (!variante) return;
    caja.classList.add('is-cargando');
    check.disabled = true;
    try {
      const data = check.checked
        ? await postJSON(`${theme.routes.cartAdd}.js`, { items: [{ id: Number(variante), quantity: 1 }], sections: [CART_SECTION], sections_url: window.location.pathname })
        : await postJSON(`${theme.routes.cartChange}.js`, { id: String(variante), quantity: 0, sections: [CART_SECTION], sections_url: window.location.pathname });
      drawer.render(data.sections);
    } catch (error) {
      check.checked = !check.checked;
      check.disabled = false;
      caja.classList.remove('is-cargando');
      drawer.showError(error.message);
    }
  });

  /* Si solo queda el Express (o el Express y el regalo), se quita también. */
  const limpiarExtrasSolos = async () => {
    const caja = document.querySelector('[data-express]');
    if (!caja || !drawer) return;
    try {
      const carrito = await fetch(`${theme.routes.cart}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const idExpress = Number(caja.dataset.expressProducto);
      const regalo = Number(document.querySelector('[data-regalo]')?.dataset.regaloId || 0);
      const reales = carrito.items.filter((l) => l.product_id !== idExpress && l.variant_id !== regalo);
      const express = carrito.items.find((l) => l.product_id === idExpress);
      if (express && reales.length === 0) {
        const data = await postJSON(`${theme.routes.cartChange}.js`, { id: express.key, quantity: 0, sections: [CART_SECTION], sections_url: window.location.pathname });
        drawer.render(data.sections);
      }
    } catch (e) { /* opcional */ }
  };
  document.addEventListener('carrito:actualizado', limpiarExtrasSolos);

  /* ---------- Cuenta atrás de la rebaja (carrito) ----------
     Cuenta hasta la fecha de Configuración del tema. Se busca el aviso en
     cada tic porque el carrito se vuelve a pintar al cambiar productos. */
  const dosCifras = (n) => String(n).padStart(2, '0');
  const ticRebaja = () => {
    document.querySelectorAll('[data-cuenta-atras]').forEach((aviso) => {
      const reloj = aviso.querySelector('[data-cuenta-reloj]');
      const fin = Date.parse(aviso.dataset.cuentaAtras || '');
      if (!reloj || Number.isNaN(fin)) return;
      let resto = Math.floor((fin - Date.now()) / 1000);
      if (resto <= 0) { reloj.hidden = true; return; }
      const d = Math.floor(resto / 86400); resto %= 86400;
      const h = Math.floor(resto / 3600); resto %= 3600;
      const m = Math.floor(resto / 60); const s = resto % 60;
      const poner = (k, v) => { const el = reloj.querySelector(`[data-cuenta="${k}"]`); if (el && el.textContent !== v) el.textContent = v; };
      poner('d', String(d)); poner('h', dosCifras(h)); poner('m', dosCifras(m)); poner('s', dosCifras(s));
      reloj.hidden = false;
    });
  };
  if (document.querySelector('[data-carrito-lateral]')) { ticRebaja(); setInterval(ticRebaja, 1000); }

  /* ---------- «Completa tu look»: 2 productos al azar de la colección ---------- */
  document.querySelectorAll('[data-completa-look]').forEach((bloque) => {
    const items = [...bloque.querySelectorAll('[data-look-item]')];
    if (items.length <= 2) return;
    for (let i = items.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [items[i], items[j]] = [items[j], items[i]];
    }
    const lista = bloque.querySelector('.recos__lista');
    items.forEach((li, i) => {
      if (i < 2) { li.hidden = false; lista.appendChild(li); } else li.remove();
    });
  });

  /* ---------- «Completa tu look» en filas: selectores de color y talla ----------
     Al cambiar color o talla se actualizan foto, precio y si queda stock.
     «Añadir» pide elegir talla si falta y añade la variante sin salir. */
  const lookVariante = (fila) => {
    const variantes = JSON.parse(fila.querySelector('[data-look-variantes]')?.textContent || '[]');
    const selects = [...fila.querySelectorAll('[data-look-opcion]')];
    const eleccion = selects.map((s) => s.value || null);
    const exacta = variantes.find((v) => v.options.every((o, i) => eleccion[i] === undefined || o === eleccion[i]));
    const orientativa = variantes.find((v) => v.options.every((o, i) => !eleccion[i] || o === eleccion[i]));
    return { variantes, selects, eleccion, exacta: eleccion.every(Boolean) ? exacta : null, orientativa };
  };
  const pintarLookFila = (fila) => {
    const { variantes, selects, eleccion, exacta, orientativa } = lookVariante(fila);
    const ref = exacta || orientativa;
    const precio = fila.querySelector('[data-look-precio]');
    if (ref && precio) precio.innerHTML = ref.price + (ref.compare ? `<s>${ref.compare}</s>` : '');
    const img = fila.querySelector('.look-fila__media img');
    if (ref?.image && img && img.getAttribute('src') !== ref.image) { img.removeAttribute('srcset'); img.src = ref.image; }
    /* Tallas sin stock para el color elegido: se marcan como agotadas. */
    selects.forEach((sel, i) => {
      [...sel.options].forEach((op) => {
        if (!op.value) return;
        const hay = variantes.some((v) => v.available && v.options[i] === op.value && v.options.every((o, j) => j === i || !eleccion[j] || o === eleccion[j]));
        op.disabled = !hay;
        op.textContent = hay ? op.value : `${op.value} · agotada`;
      });
    });
    const boton = fila.querySelector('[data-look-anadir]');
    if (boton) boton.toggleAttribute('aria-disabled', Boolean(exacta && !exacta.available));
  };
  document.querySelectorAll('[data-look-fila]').forEach(pintarLookFila);
  document.addEventListener('change', (event) => {
    const sel = event.target.closest('[data-look-opcion]');
    if (!sel) return;
    const fila = sel.closest('[data-look-fila]');
    sel.closest('.look-fila__select')?.classList.remove('is-falta');
    const aviso = fila.querySelector('[data-look-aviso]');
    if (aviso) aviso.textContent = '';
    pintarLookFila(fila);
  });
  document.addEventListener('click', async (event) => {
    const boton = event.target.closest('[data-look-anadir]');
    if (!boton) return;
    event.preventDefault();
    const fila = boton.closest('[data-look-fila]');
    const { selects, exacta } = lookVariante(fila);
    const aviso = fila.querySelector('[data-look-aviso]');
    const falta = selects.find((s) => !s.value);
    if (falta) {
      falta.closest('.look-fila__select')?.classList.add('is-falta');
      falta.focus();
      if (aviso) aviso.textContent = `Elige ${falta.closest('label')?.querySelector('.visually-hidden')?.textContent.toLowerCase() || 'una opción'}`;
      return;
    }
    if (!exacta || !exacta.available) {
      if (aviso) aviso.textContent = theme.strings.quickUnavailable || 'No disponible';
      return;
    }
    fila.classList.add('is-cargando');
    boton.setAttribute('aria-busy', 'true');
    try {
      const data = await postJSON(`${theme.routes.cartAdd}.js`, { items: [{ id: exacta.id, quantity: 1 }], sections: [CART_SECTION], sections_url: window.location.pathname });
      if (drawer) { drawer.render(data.sections); drawer.open(boton); }
      fila.classList.add('is-hecho');
      setTimeout(() => fila.classList.remove('is-hecho'), 1500);
    } catch (error) {
      if (aviso) aviso.textContent = error.message;
    } finally {
      fila.classList.remove('is-cargando');
      boton.removeAttribute('aria-busy');
    }
  });

  /* ---------- Añadir rápido (tarjetas y «Combina con») ----------
     Botón «Añadir a tu carrito» → sube un panel con muestras de color y
     botones de talla → al completar la combinación se añade y se abre el
     carrito. Con una sola variante, añade al primer toque. */
  let panelRapido = null;

  const anadirVariante = async (root, variantId, trigger) => {
    const errorBox = root.querySelector('[data-anadir-rapido-error]');
    if (!drawer) {
      const form = root.querySelector('form');
      const input = form?.querySelector('[data-anadir-rapido-id]');
      if (input) input.value = variantId;
      form?.submit();
      return false;
    }
    if (errorBox) errorBox.hidden = true;
    root.classList.add('is-cargando');
    trigger?.setAttribute('aria-busy', 'true');
    try {
      const data = await postJSON(`${theme.routes.cartAdd}.js`, {
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: [CART_SECTION],
        sections_url: window.location.pathname
      });
      drawer.render(data.sections);
      drawer.open(document.contains(trigger) ? trigger : null);
      return true;
    } catch (error) {
      const compacto = root.classList.contains('anadir-rapido--compacto');
      if (panelRapido && document.contains(panelRapido)) {
        const aviso = panelRapido.querySelector('[data-selector-aviso]');
        if (aviso) aviso.textContent = error.message;
      } else if (errorBox && document.contains(errorBox) && !compacto) {
        errorBox.textContent = error.message;
        errorBox.hidden = false;
      } else {
        drawer.showError(error.message);
      }
      return false;
    } finally {
      root.classList.remove('is-cargando');
      trigger?.removeAttribute('aria-busy');
    }
  };

  const cerrarPanelRapido = (devolverFoco = false) => {
    if (!panelRapido) return;
    const panel = panelRapido;
    const root = panel.__root;
    panelRapido = null;
    root?.querySelector('[data-anadir-rapido-abrir]')?.setAttribute('aria-expanded', 'false');
    root?.classList.remove('is-abierto');
    panel.closest('.tarjeta')?.classList.remove('tiene-selector');
    panel.classList.add('is-saliendo');
    setTimeout(() => panel.remove(), 180);
    if (devolverFoco && root && document.contains(root)) root.querySelector('[data-anadir-rapido-abrir]')?.focus();
  };

  const abrirPanelRapido = (root, boton) => {
    cerrarPanelRapido();
    const plantilla = root.querySelector('[data-anadir-rapido-plantilla]');
    const json = root.querySelector('[data-anadir-rapido-json]');
    if (!plantilla || !json) return;

    const variantes = JSON.parse(json.textContent);
    const panel = plantilla.content.firstElementChild.cloneNode(true);
    const grupos = [...panel.querySelectorAll('[data-selector-grupo]')];
    const total = grupos.length;
    const eleccion = new Array(total).fill(null);

    /* Se deja elegido todo menos la última opción (normalmente la talla),
       tomando la primera combinación con stock. Así basta un toque. */
    const primera = variantes.find((v) => v.available) || variantes[0];
    grupos.forEach((grupo, i) => {
      const valores = grupo.querySelectorAll('[data-selector-valor]');
      if (valores.length === 1) eleccion[i] = valores[0].dataset.selectorValor;
      else if (i < total - 1 && primera) eleccion[i] = primera.options[i];
    });

    const aviso = panel.querySelector('[data-selector-aviso]');
    const precio = panel.querySelector('[data-selector-precio]');
    const tarjeta = root.closest('.tarjeta');
    const imagenTarjeta = tarjeta?.querySelector('.tarjeta__imagen');

    const disponible = (i, valor) =>
      variantes.some(
        (v) => v.available && v.options[i] === valor && v.options.every((o, j) => j === i || eleccion[j] === null || o === eleccion[j])
      );

    const pintar = () => {
      grupos.forEach((grupo, i) => {
        const elegido = grupo.querySelector('[data-selector-elegido]');
        if (elegido) elegido.textContent = eleccion[i] ? `: ${eleccion[i]}` : '';
        grupo.querySelectorAll('[data-selector-valor]').forEach((b) => {
          const valor = b.dataset.selectorValor;
          b.setAttribute('aria-pressed', String(eleccion[i] === valor));
          b.classList.toggle('is-agotado', !disponible(i, valor));
        });
      });
      const exacta = eleccion.every((e) => e !== null) && variantes.find((v) => v.options.every((o, j) => o === eleccion[j]));
      const orientativa = exacta || variantes.find((v) => v.options.every((o, j) => eleccion[j] === null || o === eleccion[j]));
      if (precio && orientativa) precio.textContent = orientativa.price;
    };

    panel.addEventListener('click', async (event) => {
      if (event.target.closest('[data-selector-cerrar]')) {
        cerrarPanelRapido(true);
        return;
      }
      const b = event.target.closest('[data-selector-valor]');
      if (!b || panel.classList.contains('is-cargando')) return;
      const i = Number(b.closest('[data-selector-grupo]').dataset.selectorGrupo);
      eleccion[i] = b.dataset.selectorValor;
      if (aviso) aviso.textContent = '';

      /* Si al cambiar de color la talla ya no existe, se deja sin elegir. */
      eleccion.forEach((valor, j) => {
        if (j !== i && valor !== null && !disponible(j, valor)) eleccion[j] = null;
      });

      if (b.dataset.selectorFoto && imagenTarjeta) {
        imagenTarjeta.removeAttribute('srcset');
        imagenTarjeta.src = b.dataset.selectorFoto;
      }
      pintar();

      if (eleccion.some((e) => e === null)) return;
      const variante = variantes.find((v) => v.options.every((o, j) => o === eleccion[j]));
      if (!variante || !variante.available) {
        if (aviso) aviso.textContent = theme.strings.quickUnavailable || '';
        return;
      }
      panel.classList.add('is-cargando');
      b.classList.add('is-anadiendo');
      const ok = await anadirVariante(root, variante.id, b);
      if (!document.contains(panel)) return;
      panel.classList.remove('is-cargando');
      b.classList.remove('is-anadiendo');
      if (ok) {
        panel.classList.add('is-hecho');
        if (aviso) aviso.textContent = theme.strings.quickAdded || '';
        setTimeout(() => { if (panelRapido === panel) cerrarPanelRapido(); }, 700);
      }
    });

    /* Dónde se coloca: en el carrito, bajo la fila de «Combina con»; en las
       tarjetas, sube desde abajo cubriendo la parte baja de la tarjeta. */
    const destino = root.closest('.recos')?.querySelector('[data-recos-panel]') || tarjeta || root;
    panel.__root = root;
    destino.appendChild(panel);
    tarjeta?.classList.add('tiene-selector');
    panelRapido = panel;
    root.classList.add('is-abierto');
    boton?.setAttribute('aria-expanded', 'true');
    pintar();
    requestAnimationFrame(() => {
      panel.classList.add('is-visible');
      const primerLibre = panel.querySelector('[data-selector-grupo]:last-of-type [data-selector-valor]:not(.is-agotado)');
      (primerLibre || panel).focus({ preventScroll: true });
      if (panel.closest('.carrito-lateral')) panel.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
  };

  document.addEventListener('click', (event) => {
    const directo = event.target.closest('[data-anadir-rapido-directo]');
    if (directo) {
      event.preventDefault();
      const root = directo.closest('[data-anadir-rapido]');
      const id = root?.querySelector('[data-anadir-rapido-id]')?.value;
      if (root && id) anadirVariante(root, id, directo);
      return;
    }

    const abrir = event.target.closest('[data-anadir-rapido-abrir]');
    if (abrir) {
      event.preventDefault();
      const root = abrir.closest('[data-anadir-rapido]');
      if (!root) return;
      if (panelRapido && panelRapido.__root === root) cerrarPanelRapido();
      else abrirPanelRapido(root, abrir);
      return;
    }

    /* Clic fuera del panel: se cierra. */
    if (panelRapido && !event.target.closest('.selector-rapido') && !event.target.closest('[data-anadir-rapido]')) {
      cerrarPanelRapido();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && panelRapido) {
      event.stopPropagation();
      cerrarPanelRapido(true);
    }
  }, true);

  /* ---------- Carruseles (fotos de clientas y reseñas) ----------
     Las flechas mueven la fila un ancho de pantalla, respetando dónde
     encaja cada tarjeta, y se apagan al llegar a los extremos. */
  document.querySelectorAll('[data-carrusel]').forEach((carrusel) => {
    const pista = carrusel.querySelector('[data-carrusel-pista]');
    const anterior = carrusel.querySelector('[data-carrusel-anterior]');
    const siguiente = carrusel.querySelector('[data-carrusel-siguiente]');
    if (!pista) return;

    const paso = () => {
      const primera = pista.firstElementChild;
      if (!primera) return pista.clientWidth;
      const estilo = getComputedStyle(pista);
      const hueco = parseFloat(estilo.columnGap || estilo.gap) || 0;
      const ancho = primera.getBoundingClientRect().width + hueco;
      /* Tantas tarjetas enteras como quepan, al menos una. */
      const caben = Math.max(1, Math.floor(pista.clientWidth / ancho));
      return ancho * caben;
    };

    const refrescar = () => {
      const margen = 4;
      const maximo = pista.scrollWidth - pista.clientWidth - margen;
      const alPrincipio = pista.scrollLeft <= margen;
      const alFinal = pista.scrollLeft >= maximo;
      const sinDesplazamiento = maximo <= margen;
      if (anterior) anterior.disabled = alPrincipio || sinDesplazamiento;
      if (siguiente) siguiente.disabled = alFinal || sinDesplazamiento;
      carrusel.classList.toggle('carrusel--sin-flechas', sinDesplazamiento);
    };

    const mover = (sentido) => {
      pista.scrollBy({ left: paso() * sentido, behavior: 'smooth' });
    };

    anterior?.addEventListener('click', () => mover(-1));
    siguiente?.addEventListener('click', () => mover(1));
    pista.addEventListener('scroll', () => {
      clearTimeout(pista.__temporizador);
      pista.__temporizador = setTimeout(refrescar, 80);
    }, { passive: true });
    window.addEventListener('resize', refrescar);
    /* Las fotos cambian el ancho al cargar, así que se vuelve a mirar. */
    window.addEventListener('load', refrescar);
    refrescar();
  });

  /* ---------- Barra de compra fija ----------
     Sale cuando el bloque de compra queda por encima de la pantalla y se
     esconde al volver a verlo. Se mueve al <body> para que ningún
     contenedor la recorte ni le cambie la posición en el móvil.

     Compatible con apps de combos o packs (Pumper Bundles y similares):
     esas apps esconden el botón del tema y ponen el suyo. Un botón
     escondido mide cero y la barra no aparecía nunca. Por eso se busca el
     primer punto de referencia que de verdad ocupe sitio en la página. */
  let barraCompra = null;
  let barraPendiente = false;

  const PUNTOS_REFERENCIA = [
    '[data-producto-info] [data-boton-anadir]',
    '[data-producto-info] .producto__compra',
    '[data-producto-info] form[action*="/cart/add"]',
    '[data-producto-info] [data-producto-precio]',
    '[data-producto-info]'
  ];

  const visibleEnPagina = (el) => {
    if (!el || el.closest('[data-barra-compra]')) return false;
    const caja = el.getBoundingClientRect();
    return caja.height > 0 && caja.width > 0;
  };

  const referenciaBarra = () => {
    for (const selector of PUNTOS_REFERENCIA) {
      for (const el of document.querySelectorAll(selector)) {
        if (visibleEnPagina(el)) return el.getBoundingClientRect();
      }
    }
    return null;
  };

  /* Lo que tapa la cabecera fija (aviso + menú) desde arriba de la pantalla. */
  const altoCabeceraFija = () => Math.max(0, ...[...document.querySelectorAll('.shopify-section-group-header-group, .cabecera--fija')]
    .filter((el) => ['sticky', 'fixed'].includes(getComputedStyle(el).position))
    .map((el) => el.getBoundingClientRect().bottom));

  const revisarBarraCompra = () => {
    barraPendiente = false;
    if (!barraCompra || !document.contains(barraCompra)) return;
    const caja = referenciaBarra();
    const alto = window.innerHeight || document.documentElement.clientHeight;
    /* Sale en cuanto la clienta ha pasado el precio y se esconde mientras el
       botón de compra de la ficha está a la vista. Así siempre tiene un botón
       a mano, también mientras mira los combos o la guía de tallas.
       Sin referencia (app que lo reemplaza todo): con haber bajado una
       pantalla ya se enseña. */
    const precio = document.querySelector('[data-producto-info] [data-actualizar="precio"]');
    const cajaPrecio = precio && visibleEnPagina(precio) ? precio.getBoundingClientRect() : null;
    const botonALaVista = caja ? caja.top < alto && caja.bottom > 0 : false;
    let pasoDeLargo;
    /* «Pasado» = ya queda escondido bajo la cabecera fija, no solo fuera de la pantalla. */
    const tapaCabecera = altoCabeceraFija();
    if (cajaPrecio && caja) pasoDeLargo = cajaPrecio.bottom < tapaCabecera && !botonALaVista;
    else pasoDeLargo = caja ? caja.bottom < 0 : window.scrollY > alto;
    /* Se esconde cerca del pie para no tapar los enlaces legales. */
    const pie = document.querySelector('.pie');
    const enPie = pie ? pie.getBoundingClientRect().top < alto * 0.6 : false;
    const visible = pasoDeLargo && !enPie;
    const yaVisible = barraCompra.classList.contains('is-visible');
    if (visible === yaVisible) return;
    barraCompra.classList.toggle('is-visible', visible);
    if (visible) barraCompra.removeAttribute('inert');
    else barraCompra.setAttribute('inert', '');
    document.body.classList.toggle('con-barra-compra', visible);
    document.documentElement.style.setProperty('--barra-compra-alto', `${barraCompra.offsetHeight}px`);
  };

  const pedirRevisionBarra = () => {
    if (barraPendiente) return;
    barraPendiente = true;
    requestAnimationFrame(revisarBarraCompra);
  };

  /* «Elegir talla»: mientras la clienta no toque una talla, el botón de la
     barra la lleva al selector en vez de añadir la talla por defecto. */
  let tallaElegida = new URLSearchParams(window.location.search).has('variant');
  const necesitaTalla = () => Boolean(barraCompra?.dataset.textoElegir) && !tallaElegida
    && Boolean(document.querySelector('[data-producto-info] [data-es-talla]'));

  const etiquetarBarra = () => {
    if (!barraCompra) return;
    /* Mientras pide talla, la barra no enseña «Talla: XS» (la de por defecto). */
    barraCompra.toggleAttribute('data-pide-talla', necesitaTalla());
    /* El selector de talla de la barra sale vacío («Talla») hasta que se elige una. */
    const selTalla = barraCompra.querySelector('[data-barra-talla]');
    if (selTalla) {
      const vacia = selTalla.querySelector('[data-barra-vacia]');
      if (necesitaTalla()) selTalla.value = '';
      if (vacia) vacia.disabled = !necesitaTalla();
    }
    const boton = barraCompra.querySelector('[data-boton-fijo]');
    if (!boton || boton.getAttribute('aria-disabled') === 'true') return;
    if (!boton.dataset.textoOriginal) boton.dataset.textoOriginal = boton.textContent.trim();
    const texto = necesitaTalla() ? barraCompra.dataset.textoElegir : boton.dataset.textoOriginal;
    if (boton.textContent.trim() !== texto) boton.textContent = texto;
  };

  const marcarTallaElegida = () => {
    if (tallaElegida) return;
    tallaElegida = true;
    etiquetarBarra();
  };
  document.addEventListener('change', (event) => {
    if (event.target.closest('[data-producto-info] [data-es-talla]') || event.target.closest('.pmpr_tier_list select')) marcarTallaElegida();
  });
  /* Pumper para la propagación de sus clics: se escucha en fase de captura
     y con pointerdown, que llega antes. Elegir talla o color dentro de un
     combo cuenta como haber elegido. */
  ['pointerdown', 'click'].forEach((tipo) => {
    document.addEventListener(tipo, (event) => {
      if (event.target.closest('.pmpr_optsel_option, .pmpr_tier_list select, .pmpr_tier_list [role="option"]')) marcarTallaElegida();
    }, true);
  });

  const llevarASelectorTalla = () => {
    /* Si hay un combo marcado (no «Sin combo»), la talla se elige dentro de
       su tarjeta: se lleva ahí. */
    const tarjetaCombo = document.querySelector('.pmpr-tier-shell__card[data-selected="true"]');
    const marcoCombo = tarjetaCombo?.closest('.pmpr-tier-shell__frame');
    if (marcoCombo && !marcoCombo.hasAttribute('data-tm-sin-combo') && marcoCombo.querySelector('.pmpr_optsel_group')) {
      const destinoCombo = marcoCombo.getBoundingClientRect().top + window.scrollY - (altoCabeceraFija() || 80) - 16;
      window.scrollTo({ top: Math.max(0, destinoCombo), behavior: 'smooth' });
      marcoCombo.classList.remove('is-resaltado');
      void marcoCombo.offsetWidth;
      marcoCombo.classList.add('is-resaltado');
      setTimeout(() => marcoCombo.classList.remove('is-resaltado'), 2600);
      return true;
    }
    const grupo = document.querySelector('[data-producto-info] [data-es-talla]');
    if (!grupo) return false;
    const destino = grupo.getBoundingClientRect().top + window.scrollY - (altoCabeceraFija() || 80) - 20;
    window.scrollTo({ top: Math.max(0, destino), behavior: 'smooth' });
    grupo.classList.remove('is-resaltado');
    void grupo.offsetWidth;
    grupo.classList.add('is-resaltado');
    setTimeout(() => grupo.classList.remove('is-resaltado'), 2600);
    return true;
  };

  function actualizarBarraCompra() {
    const nueva = document.querySelector('[data-producto-info] [data-barra-compra]');
    if (nueva) {
      const estabaVisible = barraCompra?.classList.contains('is-visible');
      document.querySelectorAll('body > [data-barra-compra]').forEach((vieja) => vieja.remove());
      document.body.appendChild(nueva);
      barraCompra = nueva;
      etiquetarBarra();
      if (estabaVisible) {
        /* Tras cambiar de talla, la barra nueva aparece ya en su sitio, sin volver a subir. */
        barraCompra.style.transition = 'none';
        barraCompra.classList.remove('is-visible');
        revisarBarraCompra();
        requestAnimationFrame(() => { barraCompra.style.transition = ''; });
        if (typeof actualizarBarraCombo === 'function') actualizarBarraCombo();
        return;
      }
    } else if (!barraCompra) {
      barraCompra = document.querySelector('body > [data-barra-compra]');
    }
    etiquetarBarra();
    barraCompra?.classList.remove('is-visible');
    revisarBarraCompra();
    if (typeof actualizarBarraCombo === 'function') actualizarBarraCombo();
  }

  /* El botón de la barra fija repite el clic del botón real de la ficha.
     Si una app de combos ha puesto el suyo, se pulsa el de la app, para que
     el pack y su descuento entren en el carrito tal y como los ha elegido
     la clienta. Solo si no hay ninguno, la barra envía su propio formulario. */
  const botonRealAnadir = () => {
    const candidatos = [
      '[data-producto-info] [data-boton-anadir]',
      '[data-producto-info] form[action*="/cart/add"] button[type="submit"]',
      '[data-producto-info] form[action*="/cart/add"] [name="add"]',
      'form[action*="/cart/add"] button[type="submit"]',
      'form[action*="/cart/add"] [name="add"]'
    ];
    for (const selector of candidatos) {
      for (const el of document.querySelectorAll(selector)) {
        if (!visibleEnPagina(el)) continue;
        if (el.disabled || el.getAttribute('aria-disabled') === 'true') continue;
        return el;
      }
    }
    return null;
  };

  document.addEventListener('click', (event) => {
    const botonFijo = event.target.closest('[data-boton-fijo]');
    if (!botonFijo) return;
    if (necesitaTalla() && botonFijo.getAttribute('aria-disabled') !== 'true') {
      event.preventDefault();
      if (llevarASelectorTalla()) return;
    }
    const real = botonRealAnadir();
    if (!real) return;
    event.preventDefault();
    real.click();
  });

  /* Selectores de color y talla de la barra: marcan la misma opción en la
     ficha (el selector principal recalcula variante, precio y stock). */
  document.addEventListener('change', (event) => {
    const sel = event.target.closest('[data-barra-opcion]');
    if (!sel || !sel.value) return;
    const radios = document.querySelectorAll(`[data-producto-info] variant-selector input[type="radio"]`);
    const radio = [...radios].find((r) => r.name === sel.dataset.barraOpcion && r.value === sel.value);
    if (!radio) return;
    if (sel.hasAttribute('data-barra-talla')) marcarTallaElegida();
    if (!radio.checked) radio.click();
    else etiquetarBarra();
  });

  if (document.querySelector("[data-barra-compra]")) requestAnimationFrame(actualizarBarraCompra);
  window.addEventListener('scroll', pedirRevisionBarra, { passive: true });
  window.addEventListener('resize', pedirRevisionBarra);
  window.addEventListener('orientationchange', pedirRevisionBarra);
  /* Las apps de combos y packs montan su bloque un poco después de cargar la
     página: cuando cambia el alto, se vuelve a mirar dónde está el botón. */
  if ('ResizeObserver' in window) {
    const info = document.querySelector('[data-producto-info]');
    if (info) new ResizeObserver(pedirRevisionBarra).observe(info);
  }
  window.addEventListener('load', pedirRevisionBarra);
  /* En Personalizar, al tocar un ajuste la sección se vuelve a pintar. */
  document.addEventListener('shopify:section:load', () => {
    if (document.querySelector('[data-producto-info] [data-barra-compra]')) actualizarBarraCompra();
  });

  /* ---------- Panel lateral genérico (guía de tallas y filtros en móvil) ---------- */
  const abrirPanel = (root, trigger) => {
    root.__trigger = trigger || document.activeElement;
    root.removeAttribute('inert');
    root.classList.add('is-abierto');
    document.body.classList.add('bloqueo-scroll');
    const panel = root.querySelector('[role="dialog"]');
    requestAnimationFrame(() => panel?.focus());
    root.__onKey = (event) => {
      if (event.key === 'Escape') {
        cerrarPanel(root);
        return;
      }
      if (event.key !== 'Tab' || !panel) return;
      const items = [...panel.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', root.__onKey);
  };

  const cerrarPanel = (root) => {
    if (!root) return;
    root.classList.remove('is-abierto');
    root.setAttribute('inert', '');
    document.body.classList.remove('bloqueo-scroll');
    if (root.__onKey) document.removeEventListener('keydown', root.__onKey);
    if (root.__trigger && document.contains(root.__trigger)) root.__trigger.focus();
  };

  /* ---------- Guía de tallas pegada en texto enriquecido ----------
     Al pegar una tabla (de ChatGPT, Word…) en un metacampo de texto enriquecido,
     Shopify guarda las celdas pegadas sin separación: «TallaTalla EUPecho…» y
     «XS3482–86 cm62–66 cm…». Pero conserva en negrita la primera columna (XS,
     S, M…), y con eso se sabe dónde empieza cada fila. Aquí se vuelve a montar
     la tabla. Si algo no cuadra, se deja el texto tal cual estaba. */
  function reconstruirGuiaTallas(raiz) {
    const escapar = (t) => String(t).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

    /* Trozos de texto de un bloque, con si van en negrita o no. */
    const trozos = (nodo, negrita = false, salida = []) => {
      if (!nodo) return salida;
      if (nodo.type === 'text') {
        if (nodo.value) salida.push({ texto: nodo.value, negrita: negrita || !!nodo.bold });
      } else if (Array.isArray(nodo.children)) {
        nodo.children.forEach((hijo) => trozos(hijo, negrita, salida));
      }
      return salida;
    };

    /* «TallaTalla EUPechoCinturaCadera» → Talla | Talla EU | Pecho | Cintura | Cadera */
    const partirCabecera = (texto) =>
      texto
        .replace(/([a-záéíóúüñ])([A-ZÁÉÍÓÚÜÑ])/g, '$1|$2')
        .replace(/([A-ZÁÉÍÓÚÜÑ])([A-ZÁÉÍÓÚÜÑ][a-záéíóúüñ])/g, '$1|$2')
        .split('|')
        .map((t) => t.trim())
        .filter(Boolean);

    /* «3482–86 cm» → «34» y «82–86 cm»: el rango empieza por un número menor
       que el final y con las mismas cifras o menos. */
    const separarPrefijo = (trozo) => {
      const m = trozo.match(/^(.*?)(\d+)\s*([–-])\s*(\d+)(.*)$/);
      if (!m) return null;
      const [, antes, cifras, guion, fin, resto] = m;
      const final = Number(fin);
      for (let largo = fin.length; largo >= 1; largo -= 1) {
        if (cifras.length <= largo) continue;
        const inicio = cifras.slice(-largo);
        const numero = Number(inicio);
        if (numero < final && numero >= final * 0.5 && inicio[0] !== '0') {
          return [antes + cifras.slice(0, -largo), `${inicio}${guion}${fin}${resto}`];
        }
      }
      return null;
    };

    /* «3482–86 cm62–66 cm87–91 cm» → 4 celdas. */
    const partirValores = (texto, cuantos) => {
      let celdas = texto
        .split(/(?<=\b(?:cm|mm|kg|g|in|%|años|meses))\s*(?=[\d<>~≈+])/)
        .map((t) => t.trim())
        .filter(Boolean);
      let vueltas = 0;
      while (celdas.length < cuantos && vueltas < cuantos) {
        vueltas += 1;
        const i = celdas.findIndex((c) => separarPrefijo(c));
        if (i === -1) break;
        celdas.splice(i, 1, ...separarPrefijo(celdas[i]));
      }
      if (celdas.length === cuantos) return celdas;
      /* Plan B: «S90 cm94 cm» → S | 90 cm | 94 cm. Una talla en letras pegada
         a números, y medidas con su unidad. */
      const fichas = texto.match(/[A-ZÁÉÍÓÚÑ]{1,4}(?=\s*\d|\s*$)|\d+(?:[.,]\d+)?(?:\s*[–-]\s*\d+(?:[.,]\d+)?)?\s*(?:cm|mm|kg|g|in|%|años|meses)?/g);
      const limpias = (fichas || []).map((t) => t.trim()).filter(Boolean);
      return limpias.length === cuantos ? limpias : null;
    };

    /* Intenta leer un párrafo como tabla. */
    const comoTabla = (lista) => {
      const esEtiqueta = (t) => t.negrita && t.texto.trim().length <= 8;
      const primeras = lista.map((t, i) => (esEtiqueta(t) ? i : -1)).filter((i) => i >= 0);
      if (primeras.length < 2) return null;

      const cabeceraTrozos = lista.slice(0, primeras[0]).filter((t) => t.texto.trim());
      let cabecera = cabeceraTrozos.length > 1 ? cabeceraTrozos.map((t) => t.texto.trim()) : partirCabecera(cabeceraTrozos.map((t) => t.texto).join(''));
      if (cabecera.length < 2) return null;
      const columnas = cabecera.length;

      const filas = [];
      for (let n = 0; n < primeras.length; n += 1) {
        const desde = primeras[n];
        const hasta = n + 1 < primeras.length ? primeras[n + 1] : lista.length;
        const etiqueta = lista[desde].texto.trim();
        const resto = lista.slice(desde + 1, hasta).filter((t) => t.texto.trim());
        let valores = resto.length === columnas - 1 ? resto.map((t) => t.texto.trim()) : partirValores(resto.map((t) => t.texto).join(''), columnas - 1);
        if (!valores) return null;
        filas.push([etiqueta, ...valores]);
      }
      return (
        '<div class="tabla-scroll"><table><thead><tr>' +
        cabecera.map((c) => `<th scope="col">${escapar(c)}</th>`).join('') +
        '</tr></thead><tbody>' +
        filas.map((f) => '<tr>' + f.map((c, i) => (i ? `<td>${escapar(c)}</td>` : `<th scope="row">${escapar(c)}</th>`)).join('') + '</tr>').join('') +
        '</tbody></table></div>'
      );
    };

    const enLinea = (lista) => lista.map((t) => (t.negrita ? `<strong>${escapar(t.texto)}</strong>` : escapar(t.texto))).join('');

    let html = '';
    let tablas = 0;
    (raiz && raiz.children ? raiz.children : []).forEach((bloque) => {
      const lista = trozos(bloque);
      if (bloque.type === 'heading') {
        html += `<h3 class="texto-tabla__titulo">${enLinea(lista)}</h3>`;
        return;
      }
      if (bloque.type === 'list') {
        const etiqueta = bloque.listType === 'ordered' ? 'ol' : 'ul';
        html += `<${etiqueta}>` + (bloque.children || []).map((li) => `<li>${enLinea(trozos(li))}</li>`).join('') + `</${etiqueta}>`;
        return;
      }
      const tabla = comoTabla(lista);
      if (tabla) {
        tablas += 1;
        html += tabla;
        return;
      }
      /* Un párrafo todo en negrita y en mayúsculas es un título. */
      const texto = lista.map((t) => t.texto).join('').trim();
      if (!texto) return;
      if (lista.every((t) => t.negrita) && texto === texto.toUpperCase() && texto.length < 70) {
        html += `<h3 class="texto-tabla__titulo">${escapar(texto)}</h3>`;
      } else {
        html += `<p>${enLinea(lista)}</p>`;
      }
    });
    return tablas ? html : null;
  }

  /* Convierte el HTML del texto enriquecido de Shopify en la misma estructura
     que usa reconstruirGuiaTallas (bloques con trozos de texto y negritas). */
  function leerTextoEnriquecido(contenedor) {
    const aNodos = (el, negrita = false) => {
      const salida = [];
      el.childNodes.forEach((n) => {
        if (n.nodeType === 3) {
          if (n.textContent) salida.push({ type: 'text', value: n.textContent, bold: negrita || undefined });
        } else if (n.nodeType === 1) {
          if (n.tagName === 'BR') {
            salida.push({ type: 'text', value: '\n', bold: negrita || undefined });
            return;
          }
          const esNegrita = negrita || n.tagName === 'STRONG' || n.tagName === 'B';
          salida.push(...aNodos(n, esNegrita));
        }
      });
      return salida;
    };
    const bloques = [];
    [...contenedor.children].forEach((el) => {
      const tag = el.tagName;
      if (/^H[1-6]$/.test(tag)) bloques.push({ type: 'heading', children: aNodos(el) });
      else if (tag === 'UL' || tag === 'OL') bloques.push({ type: 'list', listType: tag === 'OL' ? 'ordered' : 'unordered', children: [...el.children].map((li) => ({ type: 'list-item', children: aNodos(li) })) });
      else bloques.push({ type: 'paragraph', children: aNodos(el) });
    });
    return { type: 'root', children: bloques };
  }

  /* Se aplica a la guía de tallas y a los desplegables de la ficha. Solo se
     cambia el contenido si de verdad se encuentra una tabla. */
  document.querySelectorAll('[data-guia] .metafield-rich_text_field, .detalles__item .metafield-rich_text_field').forEach((contenedor) => {
    try {
      const html = reconstruirGuiaTallas(leerTextoEnriquecido(contenedor));
      if (html) contenedor.innerHTML = html;
    } catch (error) {
      /* Si algo falla, se queda el texto como estaba. */
    }
  });

  /* ---------- Desplegables de la ficha: uno abierto a la vez ----------
     Los navegadores nuevos ya lo hacen con el atributo name; esto cubre
     los que no. */
  document.addEventListener('toggle', (event) => {
    const abierto = event.target;
    if (!(abierto instanceof HTMLDetailsElement) || !abierto.open || !abierto.dataset.acordeon) return;
    document.querySelectorAll(`details[data-acordeon="${abierto.dataset.acordeon}"][open]`).forEach((otro) => {
      if (otro !== abierto) otro.open = false;
    });
  }, true);

  /* ---------- Fotos de Pumper a su tamaño ----------
     Pumper pinta las miniaturas de los combos con la foto original (941×1672,
     ~200 KB cada una) aunque se ven a 40-100 px. Como son de la CDN de Shopify,
     basta con pedirlas con ?width=… Sus fotos van con loading="lazy", así que
     cambiar el src antes de que entren en pantalla no descarga nada dos veces. */
  const ANCHO_FOTO_PUMPER = 180;
  const achicarFotoPumper = (img) => {
    const src = img.getAttribute('src') || '';
    if (!src || /[?&]width=/.test(src)) return;
    if (!/cdn\.shopify\.com\/s\/files\/|\/cdn\/shop\/files\//.test(src)) return;
    img.setAttribute('src', src + (src.includes('?') ? '&' : '?') + 'width=' + ANCHO_FOTO_PUMPER);
    if (img.hasAttribute('srcset')) img.removeAttribute('srcset');
  };
  const esZonaPumper = (el) => el.closest && el.closest('[class*="pmpr"], [class*="pumper"], [id*="pumper"]');
  const revisarFotosPumper = (raiz) => {
    if (raiz.tagName === 'IMG') { if (esZonaPumper(raiz)) achicarFotoPumper(raiz); return; }
    raiz.querySelectorAll?.('img').forEach((img) => { if (esZonaPumper(img)) achicarFotoPumper(img); });
  };
  if ('MutationObserver' in window && document.querySelector('[data-producto-info]')) {
    new MutationObserver((cambios) => {
      cambios.forEach((c) => {
        if (c.type === 'attributes') revisarFotosPumper(c.target);
        else c.addedNodes.forEach((n) => n.nodeType === 1 && revisarFotosPumper(n));
      });
    }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['src'] });
    revisarFotosPumper(document.body);
  }

  /* ---------- Carrusel infinito: se para al tocarlo en el móvil ---------- */
  document.querySelectorAll('.cinta').forEach((cinta) => {
    cinta.addEventListener('touchstart', () => cinta.classList.add('is-pausada'), { passive: true });
    cinta.addEventListener('touchend', () => setTimeout(() => cinta.classList.remove('is-pausada'), 1500), { passive: true });
  });

  /* Las fotos y vídeos de la cinta llegan sin src para no robar ancho de
     banda a la foto principal. Se cargan todos de golpe cuando la cinta está
     a menos de una pantalla. */
  const cargarCinta = (cinta) => {
    if (cinta.dataset.cargada) return;
    cinta.dataset.cargada = '1';
    cinta.querySelectorAll('[data-srcset]').forEach((el) => { el.srcset = el.dataset.srcset; el.removeAttribute('data-srcset'); });
    cinta.querySelectorAll('[data-src]').forEach((el) => {
      el.src = el.dataset.src;
      el.removeAttribute('data-src');
      if (el.tagName === 'VIDEO') { el.autoplay = true; el.play?.().catch(() => {}); }
    });
  };
  const cintas = document.querySelectorAll('.cinta');
  if (cintas.length) {
    if ('IntersectionObserver' in window) {
      const vigia = new IntersectionObserver((entradas) => {
        entradas.forEach((e) => { if (e.isIntersecting) { cargarCinta(e.target); vigia.unobserve(e.target); } });
      }, { rootMargin: '0px 0px 100% 0px' });
      cintas.forEach((c) => vigia.observe(c));
    } else {
      cintas.forEach(cargarCinta);
    }
  }

  /* ---------- Productos destacados al azar ----------
     El tema prepara una reserva de productos (de la colección y de sus
     subcolecciones) y aquí se eligen al azar los que se ven. */
  document.querySelectorAll('[data-destacados-mezclar]').forEach((lista) => {
    const cuantos = Number(lista.dataset.destacadosMezclar) || 4;
    const items = [...lista.children];
    for (let i = items.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [items[i], items[j]] = [items[j], items[i]];
    }
    items.forEach((item, i) => {
      item.hidden = i >= cuantos;
      lista.appendChild(item);
    });
  });

  /* ---------- Preferencias de cookies (aviso de Shopify) ----------
     El enlace del pie reabre el aviso de cookies de Shopify. Solo se enseña
     si ese aviso está activado en la tienda. */
  const botonCookies = document.querySelector('[data-preferencias-cookies]');
  if (botonCookies) {
    const activar = () => {
      if (window.privacyBanner && typeof window.privacyBanner.showPreferences === 'function') {
        botonCookies.hidden = false;
        return true;
      }
      return false;
    };
    if (!activar()) {
      document.addEventListener('visitorConsentCollected', activar);
      window.addEventListener('load', () => setTimeout(activar, 1500));
    }
    botonCookies.addEventListener('click', () => window.privacyBanner?.showPreferences());
  }

  /* ---------- Guía de tallas ---------- */
  document.addEventListener('click', (event) => {
    const abrir = event.target.closest('[data-guia-abrir]');
    if (abrir) {
      const panel = document.getElementById(abrir.getAttribute('aria-controls'));
      if (panel) abrirPanel(panel, abrir);
      return;
    }
    const cerrar = event.target.closest('[data-guia-cerrar]');
    if (cerrar) cerrarPanel(cerrar.closest('[data-guia]'));
  });

  /* ---------- Cantidad de la ficha ---------- */
  document.addEventListener('click', (event) => {
    const paso = event.target.closest('[data-cantidad-paso]');
    if (!paso) return;
    const input = paso.closest('.cantidad')?.querySelector('[data-cantidad-input]');
    if (!input) return;
    input.value = Math.max(1, (parseInt(input.value, 10) || 1) + Number(paso.dataset.cantidadPaso));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- Galería de la ficha ---------- */
  /* «Azul Claro», «azul claro», «azul-claro» → «azul-claro». */
  const claveColor = (texto) => String(texto || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

  /* Número de orden de una foto para un color: «azul-claro-2» → 2,
     «azul-claro» → 0. Si no es de ese color, null. («rosa» no se queda con
     las fotos de «rosa-claro».) */
  const ordenParaColor = (alt, color) => {
    const a = claveColor(alt);
    if (!color || !a) return null;
    if (a === color) return 0;
    if (!a.startsWith(`${color}-`)) return null;
    const resto = a.slice(color.length + 1);
    return /^\d+$/.test(resto) ? Number(resto) : null;
  };

  /* Deja a la vista solo las fotos del color elegido y en el orden de su
     número. Si ninguna coincide, se ven todas en su orden original. */
  const filtrarGaleriaPorColor = (colorBruto) => {
    const pista = document.querySelector('[data-galeria-slides]');
    if (!pista) return;
    const color = claveColor(colorBruto);
    const slides = [...pista.querySelectorAll('[data-media-id]')];
    const listaMinis = document.querySelector('[data-galeria-miniaturas]');
    const miniaturas = [...document.querySelectorAll('[data-ir-a-media]')];
    if (!slides.length) return;

    if (!pista.__ordenOriginal) pista.__ordenOriginal = slides.map((s) => s.dataset.mediaId);
    const original = pista.__ordenOriginal;
    const orden = new Map();
    slides.forEach((s) => {
      const n = ordenParaColor(s.dataset.alt, color);
      if (n !== null) orden.set(s.dataset.mediaId, n);
    });
    const hay = orden.size > 0;
    const ids = [...original].sort((x, y) => {
      if (!hay) return 0;
      const ox = orden.has(x) ? orden.get(x) : Infinity;
      const oy = orden.has(y) ? orden.get(y) : Infinity;
      if (ox !== oy) return ox - oy;
      return original.indexOf(x) - original.indexOf(y);
    });

    /* Solo se mueven las fotos si el orden cambia. Al abrir la ficha el
       servidor ya las trae en orden: moverlas otra vez obligaría a pintar de
       nuevo la foto principal y el navegador contaría ese repintado como la
       carga de la foto (LCP), segundos más tarde. */
    const ordenActual = slides.map((s) => s.dataset.mediaId);
    const mismoOrden = ids.every((id, i) => ordenActual[i] === id);

    ids.forEach((id) => {
      const slide = slides.find((s) => s.dataset.mediaId === id);
      if (!slide) return;
      const oculta = hay && !orden.has(id);
      if (slide.hidden !== oculta) slide.hidden = oculta;
      if (!mismoOrden) pista.appendChild(slide);
      const mini = miniaturas.find((m) => m.dataset.irAMedia === id);
      const li = mini ? (mini.closest('li') || mini) : null;
      if (li) {
        if (li.hidden !== oculta) li.hidden = oculta;
        if (!mismoOrden && listaMinis && li.parentElement === listaMinis) listaMinis.appendChild(li);
      }
    });

    /* Deja la primera foto visible como la que se ve. */
    const primera = [...pista.querySelectorAll('[data-media-id]')].find((s) => !s.hidden);
    if (typeof actualizarContadorGaleria === 'function') actualizarContadorGaleria(primera);
    if (primera && !(mismoOrden && pista.scrollLeft === 0 && primera === slides[0])) {
      pista.scrollTo({ left: primera.offsetLeft - pista.offsetLeft, behavior: 'auto' });
      miniaturas.forEach((b) => {
        if (b.dataset.irAMedia === primera.dataset.mediaId) b.setAttribute('aria-current', 'true');
        else b.removeAttribute('aria-current');
      });
    }
  };

  const colorElegido = () => document.querySelector('.variantes__radio[data-color]:checked')?.dataset.color || '';

  /* Contador «2 / 7» de la galería en móvil (solo cuenta las fotos visibles). */
  const contadorGaleria = document.querySelector('[data-galeria-contador]');
  function actualizarContadorGaleria(actual) {
    if (!contadorGaleria) return;
    const pista = document.querySelector('[data-galeria-slides]');
    const visibles = pista ? [...pista.querySelectorAll('[data-media-id]')].filter((s) => !s.hidden) : [];
    const posicion = Math.max(0, visibles.indexOf(actual || visibles[0])) + 1;
    contadorGaleria.textContent = `${posicion} / ${visibles.length}`;
    contadorGaleria.hidden = visibles.length < 2;
  }

  const galeria = document.querySelector('[data-galeria-slides]');
  if (galeria) {
    filtrarGaleriaPorColor(colorElegido());
    const miniaturas = [...document.querySelectorAll('[data-ir-a-media]')];

    const marcar = (id) => {
      miniaturas.forEach((boton) => {
        if (boton.dataset.irAMedia === String(id)) boton.setAttribute('aria-current', 'true');
        else boton.removeAttribute('aria-current');
      });
    };

    miniaturas.forEach((boton) => {
      boton.addEventListener('click', () => {
        const slide = galeria.querySelector(`[data-media-id="${boton.dataset.irAMedia}"]`);
        if (!slide) return;
        galeria.scrollTo({ left: slide.offsetLeft - galeria.offsetLeft, behavior: 'smooth' });
        marcar(boton.dataset.irAMedia);
      });
    });

    if ((miniaturas.length || contadorGaleria) && 'IntersectionObserver' in window) {
      const observador = new IntersectionObserver(
        (entradas) => {
          entradas.forEach((entrada) => {
            if (!entrada.isIntersecting) return;
            marcar(entrada.target.dataset.mediaId);
            actualizarContadorGaleria(entrada.target);
          });
        },
        { root: galeria, threshold: 0.6 }
      );
      galeria.querySelectorAll('[data-media-id]').forEach((slide) => observador.observe(slide));
    }
  }

  /* ---------- Selector de variantes ---------- */
  /* ---------- Compatibilidad con Pumper Bundles ----------
     Los combos de Pumper llevan dentro el producto que se está viendo con sus
     propios desplegables de color y talla, y arrancan en la variante por
     defecto. Aquí se ponen en la variante que la clienta ha elegido en la
     ficha: al cambiar de variante, al abrir un combo y cuando la app termina
     de cargar. Solo se toca la fila de ESTE producto (se reconoce por su
     nombre); el resto de prendas del combo se quedan como estén. */
  const normalizarTexto = (t) => String(t || '').trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  let varianteActual = null;

  /* Fila de una prenda dentro de un combo: el primer contenedor que tiene la
     foto de la prenda. Las miniaturas de los desplegables de color no cuentan
     (si no, la «fila» sería el propio desplegable). */
  const esFotoDePrenda = (img) => !img.closest('.pmpr_optsel_group');
  const filaDePrenda = (el, tope) => {
    let fila = el.parentElement;
    while (fila && fila !== tope && ![...fila.querySelectorAll('img')].some(esFotoDePrenda)) fila = fila.parentElement;
    return fila && fila !== tope ? fila : null;
  };

  /* Marca la tarjeta «Sin combo» de la app: sus desplegables repiten los de
     la ficha y se esconden por CSS. Es un atributo, que la app no borra. */
  const NOMBRES_SIN_COMBO = ['sin combo', 'sin pack', 'solo este producto', 'solo 1', 'individual'];
  const marcarSinCombo = () => {
    document.querySelectorAll('.pmpr-tier-shell__frame').forEach((marco) => {
      const nombre = normalizarTexto(marco.querySelector('.pmpr-tier-title')?.textContent);
      if (NOMBRES_SIN_COMBO.includes(nombre)) marco.setAttribute('data-tm-sin-combo', '');
      else marco.removeAttribute('data-tm-sin-combo');
    });
  };

  const sincronizarCombos = () => {
    marcarSinCombo();
    const variante = varianteActual;
    if (!variante) return;
    const titulo = normalizarTexto(document.querySelector('[data-producto-info] h1')?.textContent).slice(0, 10);
    if (!titulo) return;
    const objetivos = variante.options.map(normalizarTexto);

    /* Miniaturas de ESTE producto dentro de los combos (también en los combos
       cerrados, que es lo primero que ve la clienta): foto de la variante
       elegida en vez de la de por defecto. */
    if (variante.imagen) {
      document.querySelectorAll('.pmpr_tier_list img').forEach((img) => {
        if (!esFotoDePrenda(img)) return;
        let fila = img.parentElement;
        for (let i = 0; i < 5 && fila && normalizarTexto(fila.textContent).length < 15; i += 1) fila = fila.parentElement;
        if (!fila || !normalizarTexto(fila.textContent).includes(titulo)) return;
        /* Si en la fila hay más de una prenda, no es la fila de una sola. */
        if ([...fila.querySelectorAll('img')].filter(esFotoDePrenda).length > 1) return;
        if (img.getAttribute('src') === variante.imagen) return;
        img.removeAttribute('srcset');
        img.setAttribute('src', variante.imagen);
      });
    }

    document.querySelectorAll('.pmpr_optsel_group').forEach((grupo) => {
      /* La fila del producto es el primer contenedor con la foto de la prenda. */
      const fila = filaDePrenda(grupo, grupo.closest('.pmpr_tier_list'));
      if (!fila) return;
      if (!normalizarTexto(fila.textContent).includes(titulo)) return;

      const actual = normalizarTexto(grupo.querySelector('.pmpr_optsel_value')?.textContent);
      const opcion = [...grupo.querySelectorAll('.pmpr_optsel_option')].find((o) => objetivos.includes(normalizarTexto(o.textContent)));
      if (!opcion || normalizarTexto(opcion.textContent) === actual) return;
      grupo.querySelector('.pmpr_optsel_trigger')?.click();
      opcion.click();
    });
  };

  /* La barra fija enseña el combo elegido (nombre y precio del pack) en vez
     del precio de la prenda sola. Con el primer combo («Sin combo») vuelve a
     enseñar la variante y su precio. */
  function escaparHTML(t) {
    return String(t).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  /* Declarada como función para poder llamarla desde la barra fija, que se
     prepara antes en el archivo. */
  function actualizarBarraCombo() {
    const barra = document.querySelector('body > [data-barra-compra]') || document.querySelector('[data-barra-compra]');
    if (!barra) return;
    const precioEl = barra.querySelector('.barra-compra__precio');
    const varianteEl = barra.querySelector('.barra-compra__variante');
    const tituloEl = barra.querySelector('.barra-compra__titulo');
    if (precioEl && precioEl.dataset.original === undefined) precioEl.dataset.original = precioEl.innerHTML;
    if (varianteEl && varianteEl.dataset.original === undefined) varianteEl.dataset.original = varianteEl.innerHTML;
    if (tituloEl && tituloEl.dataset.original === undefined) tituloEl.dataset.original = tituloEl.innerHTML;

    const restaurar = () => {
      if (precioEl && precioEl.dataset.original !== undefined) precioEl.innerHTML = precioEl.dataset.original;
      if (varianteEl && varianteEl.dataset.original !== undefined) varianteEl.innerHTML = varianteEl.dataset.original;
      if (tituloEl && tituloEl.dataset.original !== undefined) tituloEl.innerHTML = tituloEl.dataset.original;
      barra.classList.remove('barra-compra--pack');
    };

    const tarjetas = [...document.querySelectorAll('.pmpr-tier-shell__card')];
    const elegida = tarjetas.find((t) => t.dataset.selected === 'true');
    if (!elegida) {
      restaurar();
      return;
    }

    /* Prendas del combo elegido, cada una con su color y talla tal y como
       están en los desplegables de Pumper. La fila de una prenda es el primer
       contenedor con su foto (las miniaturas de los desplegables no cuentan). */
    const esFoto = (img) => !img.closest('.pmpr_optsel_group');
    const filaDe = (el) => {
      let fila = el.parentElement;
      while (fila && fila !== elegida && ![...fila.querySelectorAll('img')].some(esFoto)) fila = fila.parentElement;
      return fila && fila !== elegida ? fila : null;
    };
    const filas = [];
    elegida.querySelectorAll('img').forEach((img) => {
      if (!esFoto(img)) return;
      const fila = filaDe(img) || img.parentElement;
      /* Se sube hasta que la fila incluya también sus desplegables o su nombre. */
      let f = fila;
      for (let i = 0; i < 4 && f && f !== elegida && !f.querySelector('.pmpr_optsel_group') && f.textContent.trim().length < 12; i += 1) f = f.parentElement;
      if (f && f !== elegida && !filas.includes(f)) filas.push(f);
    });

    const prendas = filas.map((fila) => {
      const hojas = [...fila.querySelectorAll('*')].filter((el) => !el.children.length && !el.closest('.pmpr_optsel_group'));
      const textos = hojas.map((el) => el.textContent.trim());
      const nombre = textos.find((t) => t.length > 6 && !/[€$]|\d+[.,]\d{2}/.test(t) && !/^\d+\s*x$/i.test(t)) || '';
      const cantidad = parseInt(textos.find((t) => /^\d+\s*x$/i.test(t)), 10) || 1;
      const opciones = [...fila.querySelectorAll('.pmpr_optsel_group')]
        .map((g) => g.querySelector('.pmpr_optsel_value')?.textContent.trim())
        .filter(Boolean);
      const conPrecio = /[€$]|\d[.,]\d{2}/.test(fila.textContent);
      return { nombre, cantidad, opciones, conPrecio };
    }).filter((p) => {
      /* Fuera las ventajas del combo que Pumper pinta con icono («+ Envío
         gratuito», «Regalo»…): no son prendas, no tienen opciones ni precio. */
      if (p.opciones.length) return true;
      if (/gratis|gratuit|regalo|free|gift/i.test(p.nombre)) return false;
      return p.conPrecio;
    });

    /* «Sin combo»: se queda el precio y el nombre del producto; solo se
       actualiza el color y la talla si la clienta los ha cambiado ahí. */
    if (elegida === tarjetas[0]) {
      restaurar();
      const propia = prendas.find((p) => p.opciones.length);
      if (varianteEl && propia) varianteEl.innerHTML = formatearOpciones(propia.opciones, barra.dataset.opciones);
      return;
    }

    /* Combo: una línea por prenda, «Producto 1 · Negro · L» y debajo
       «+ Producto 2 · Azul claro · S». */
    const tituloProducto = tituloEl?.dataset.original ? tituloEl.dataset.original.replace(/<[^>]+>/g, '').trim() : '';
    if (tituloEl && prendas.length) {
      tituloEl.innerHTML = prendas
        .map((p, i) => {
          const nombre = p.nombre || tituloProducto;
          const opciones = p.opciones.length ? `<span class="barra-compra__opciones">${p.opciones.map(escaparHTML).join(' · ')}</span>` : '';
          return `<span class="barra-compra__linea"><span class="barra-compra__nombre">${i ? '+ ' : ''}${p.cantidad > 1 ? `${p.cantidad}× ` : ''}${escaparHTML(nombre)}</span>${opciones}</span>`;
        })
        .join('');
      barra.classList.add('barra-compra--pack');
    }

    /* Precio del combo: el primer importe de la tarjeta que no es una
       etiqueta de descuento; el tachado es el precio sin combo. */
    const importes = [...elegida.querySelectorAll('*')].filter(
      (el) => !el.children.length && /\d/.test(el.textContent) && /[€$]|EUR|MXN|CLP/.test(el.textContent) && !el.closest('.pmpr-tier-label') && !el.closest('.pmpr_optsel_group')
    );
    const total = importes.find((el) => el.tagName !== 'S');
    const antes = importes.find((el) => el.tagName === 'S');
    if (total && precioEl) {
      precioEl.innerHTML = `<div class="precio${antes ? ' precio--oferta' : ''}"><span class="precio__ahora">${escaparHTML(total.textContent.trim())}</span>${antes ? `<s class="precio__antes">${escaparHTML(antes.textContent.trim())}</s>` : ''}</div>`;
    }
    const nombreCombo = elegida.querySelector('.pmpr-tier-title')?.textContent.trim();
    if (varianteEl && nombreCombo) varianteEl.textContent = nombreCombo;
  }

  /* «Color: Negro · Talla: L» usando los nombres de las opciones del producto. */
  function formatearOpciones(valores, nombres) {
    const etiquetas = String(nombres || '').split('|').filter(Boolean);
    return valores
      .map((valor, i) => (etiquetas[i] ? `<span class="barra-compra__opcion">${escaparHTML(etiquetas[i])}: <strong>${escaparHTML(valor)}</strong></span>` : `<strong>${escaparHTML(valor)}</strong>`))
      .join('<span aria-hidden="true"> · </span>');
  }

  /* Cuando la app cambia el combo marcado, se actualiza la barra. */
  const vigilarComboElegido = () => {
    const info = document.querySelector('[data-producto-info]');
    if (!info || !('MutationObserver' in window)) return;
    new MutationObserver((cambios) => {
      if (cambios.some((c) => c.attributeName === 'data-selected' || c.type === 'childList' || c.type === 'characterData')) {
        clearTimeout(vigilarComboElegido.temporizador);
        vigilarComboElegido.temporizador = setTimeout(actualizarBarraCombo, 80);
      }
    }).observe(info, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['data-selected'] });
  };

  /* Variante de ESTE producto elegida dentro del combo marcado (con los
     desplegables de Pumper). Devuelve null si no hay combos o no se puede
     saber. */
  function varianteDelCombo() {
    const tarjetas = [...document.querySelectorAll('.pmpr-tier-shell__card')];
    const elegida = tarjetas.find((t) => t.dataset.selected === 'true');
    if (!elegida) return null;
    const grupos = [...elegida.querySelectorAll('.pmpr_optsel_group')];
    if (!grupos.length) return null;

    const filaDe = (grupo) => filaDePrenda(grupo, elegida) || elegida;
    const titulo = normalizarTexto(document.querySelector('[data-producto-info] h1')?.textContent).slice(0, 10);
    let propios = grupos.filter((g) => {
      const fila = filaDe(g);
      return fila && fila !== elegida && normalizarTexto(fila.textContent).includes(titulo);
    });
    /* En «Sin combo» solo está este producto, a veces sin su nombre al lado. */
    if (!propios.length && new Set(grupos.map(filaDe)).size === 1) propios = grupos;
    if (!propios.length) return null;

    const valores = propios.map((g) => normalizarTexto(g.querySelector('.pmpr_optsel_value')?.textContent));
    let variantes = [];
    try {
      variantes = JSON.parse(document.querySelector('[data-producto-info] [data-variantes-json]')?.textContent || '[]');
    } catch (error) {
      return null;
    }
    return (
      variantes.find(
        (v) => v.options.length === valores.length && v.options.every((o) => valores.includes(normalizarTexto(o)))
      ) || null
    );
  }

  /* Justo antes de añadir al carrito (botón de la ficha o barra fija), el
     formulario del tema toma la variante elegida dentro del combo. Pumper
     solo convierte la compra en combo cuando la variante que envía el tema
     coincide con la del combo; si la clienta había cambiado la talla o el
     color en los desplegables del combo, no coincidía y entraba una prenda
     suelta en la variante por defecto. */
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!form.matches || !form.matches('[data-formulario-producto]')) return;
    const variante = varianteDelCombo();
    if (!variante) return;
    const campo = form.querySelector('[name="id"]');
    if (campo && String(campo.value) !== String(variante.id)) {
      campo.value = variante.id;
      varianteActual = variante;
    }
  }, true);

  /* Se repite un par de veces porque la app redibuja con un pequeño retraso. */
  const pedirSincronizarCombos = () => {
    [60, 400, 1200].forEach((espera) => setTimeout(sincronizarCombos, espera));
  };

  /* Al elegir otro combo (clic en su tarjeta, no en sus desplegables). */
  document.addEventListener('click', (event) => {
    if (!event.target.closest('.pmpr_tier_list')) return;
    if (event.target.closest('.pmpr_optsel_group')) return;
    pedirSincronizarCombos();
  });

  /* Cuando la app monta sus combos por primera vez. */
  const vigilarMontajeCombos = () => {
    const info = document.querySelector('[data-producto-info]');
    if (!info) return;
    /* Si la app ya estaba montada cuando llega el tema, se sincroniza ya. */
    if (document.querySelector('.pmpr_optsel_group')) {
      pedirSincronizarCombos();
      return;
    }
    if (!('MutationObserver' in window)) return;
    let hecho = false;
    const observador = new MutationObserver(() => {
      if (hecho || !document.querySelector('.pmpr_optsel_group')) return;
      hecho = true;
      observador.disconnect();
      pedirSincronizarCombos();
    });
    observador.observe(info, { childList: true, subtree: true });
    setTimeout(() => observador.disconnect(), 15000);
  };

  /* Avisa a las apps de que ha cambiado la variante, por todas las vías
     habituales: el campo «id» de los formularios de la ficha, la URL y
     eventos con los nombres que usan los temas de Shopify. */
  const avisarCambioVariante = (info, variante) => {
    varianteActual = variante;
    document.querySelectorAll('form[action*="/cart/add"] [name="id"]').forEach((campo) => {
      if (campo.closest('[data-anadir-rapido]') || campo.closest('.carrito-lateral')) return;
      if (campo.closest('[data-barra-compra]') || (info && info.contains(campo))) {
        campo.value = variante.id;
        campo.dispatchEvent(new Event('input', { bubbles: true }));
        campo.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
    const detalle = { variant: variante, variantId: variante.id, id: variante.id };
    ['variant:change', 'variant:changed', 'variantChange', 'theme:variant:change', 'product:variant-change'].forEach((nombre) => {
      document.dispatchEvent(new CustomEvent(nombre, { detail: detalle, bubbles: true }));
    });
    pedirSincronizarCombos();
  };

  class VariantSelector extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-variantes-json]');
      if (!json) return;
      this.variants = JSON.parse(json.textContent);
      this.addEventListener('change', () => this.onChange());
      this.marcarNoDisponibles();
      /* Variante con la que arranca la ficha, para los combos de las apps. */
      varianteActual = this.variantePara(this.seleccion) || varianteActual;
      /* Si la clienta tocó una talla o un color antes de que cargara este
         script (o el navegador restauró la selección al volver atrás), el
         campo oculto aún lleva la variante inicial: se sincroniza ya, para
         que «Añadir al carrito» añada lo que se ve marcado. */
      const entradaInicial = this.closest('[data-producto-info]')?.querySelector('[data-input-variante]');
      const varianteMarcada = this.variantePara(this.seleccion);
      if (entradaInicial && varianteMarcada && String(varianteMarcada.id) !== entradaInicial.value) this.onChange();
      if (this.closest('[data-producto-info]')) {
        vigilarMontajeCombos();
        vigilarComboElegido();
      }
    }

    get seleccion() {
      return [...this.querySelectorAll('.variantes__radio:checked')]
        .sort((a, b) => Number(a.dataset.indice) - Number(b.dataset.indice))
        .map((input) => input.value);
    }

    variantePara(valores) {
      return this.variants.find((v) => v.options.every((opcion, i) => opcion === valores[i]));
    }

    marcarNoDisponibles() {
      const valores = this.seleccion;
      this.querySelectorAll('.variantes__radio').forEach((input) => {
        const indice = Number(input.dataset.indice) - 1;
        const existe = this.variants.some(
          (v) => v.available && v.options.every((opcion, i) => (i === indice ? opcion === input.value : opcion === valores[i]))
        );
        input.nextElementSibling?.classList.toggle('variantes__boton--agotado', !existe);
      });
      this.querySelectorAll('[data-valor-opcion]').forEach((span, i) => {
        if (valores[i]) span.textContent = valores[i];
      });
    }

    async onChange() {
      this.marcarNoDisponibles();
      filtrarGaleriaPorColor(colorElegido());
      const info = this.closest('[data-producto-info]');
      const variante = this.variantePara(this.seleccion);

      if (!variante) {
        const boton = info?.querySelector('[data-boton-anadir]');
        if (boton) {
          boton.setAttribute('aria-disabled', 'true');
          boton.textContent = theme.strings.variantUnavailable;
        }
        return;
      }

      const entrada = info?.querySelector('[data-input-variante]');
      if (entrada) entrada.value = variante.id;

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variante.id);
      window.history.replaceState({}, '', url);

      /* Aviso para las apps (Pumper Bundles, packs, upsells…): la mayoría
         escuchan el cambio del campo «id» del formulario o estos eventos. */
      avisarCambioVariante(info, variante);

      try {
        const respuesta = await fetch(`${this.dataset.url}?variant=${variante.id}&section_id=${this.dataset.seccion}`);
        if (!respuesta.ok) return;
        const fresco = parseHTML(await respuesta.text()).querySelector('[data-producto-info]');
        if (!fresco || !info) return;

        /* Solo se cambian las piezas que dependen de la variante (precio,
           aviso de stock, botón y barra fija). Antes se repintaba toda la
           columna y eso borraba los bloques de apps, como el de combos de
           Pumper, que volvía a su estado inicial o desaparecía. */
        fresco.querySelectorAll('[data-actualizar]').forEach((nueva) => {
          const actual = info.querySelector(`[data-actualizar="${nueva.dataset.actualizar}"]`);
          if (actual) actual.replaceWith(nueva);
        });

        /* El botón del tema no se sustituye (una app puede haberlo escondido):
           solo se le cambia el texto y si se puede pulsar. */
        const botonNuevo = fresco.querySelector('[data-boton-anadir]');
        const botonActual = info.querySelector('[data-boton-anadir]');
        if (botonNuevo && botonActual) {
          botonActual.textContent = botonNuevo.textContent;
          if (botonNuevo.hasAttribute('aria-disabled')) botonActual.setAttribute('aria-disabled', 'true');
          else botonActual.removeAttribute('aria-disabled');
        }

        /* La barra fija nueva entra en lugar de la que hay en el <body>. */
        const barraNueva = fresco.querySelector('[data-barra-compra]');
        if (barraNueva) {
          info.querySelectorAll('[data-barra-compra]').forEach((b) => b.remove());
          info.appendChild(barraNueva);
          actualizarBarraCompra();
        }
        filtrarGaleriaPorColor(colorElegido());
      } catch (error) {
        /* Si falla la red, el formulario envía igualmente la variante marcada. */
      }
    }
  }
  if (!customElements.get('variant-selector')) customElements.define('variant-selector', VariantSelector);

  /* ---------- Filtros y ordenación ---------- */
  const zonaFacetas = document.querySelector('[data-facetas-zona]');
  if (zonaFacetas) {
    const form = zonaFacetas.querySelector('[data-facetas]');
    const resultados = document.querySelector('[data-resultados]');
    /* En la página de colección no hay sección que recargar (la plantilla es
       Liquid), así que los filtros navegan de forma normal. */
    const seccionId = resultados?.id.replace('ResultadosProductos-', '');
    const recargaCompleta = seccionId === 'coleccion';
    const escritorio = window.matchMedia('(min-width: 990px)');

    const sincronizarModo = () => {
      if (escritorio.matches) {
        form.removeAttribute('inert');
        form.classList.remove('is-abierto');
        document.body.classList.remove('bloqueo-scroll');
      } else if (!form.classList.contains('is-abierto')) {
        form.setAttribute('inert', '');
      }
    };
    sincronizarModo();
    escritorio.addEventListener('change', sincronizarModo);

    zonaFacetas.addEventListener('click', (event) => {
      const abrir = event.target.closest('[data-facetas-abrir]');
      if (abrir) {
        abrirPanel(form, abrir);
        abrir.setAttribute('aria-expanded', 'true');
        return;
      }
      const cerrar = event.target.closest('[data-facetas-cerrar]');
      const aplicar = event.target.closest('[data-facetas-aplicar]');
      if (cerrar || (aplicar && !escritorio.matches)) {
        if (cerrar) event.preventDefault();
        cerrarPanel(form);
        zonaFacetas.querySelector('[data-facetas-abrir]')?.setAttribute('aria-expanded', 'false');
      }
    });

    const aplicar = async (url, empujarHistorial = true) => {
      if (!resultados || !seccionId || recargaCompleta) {
        window.location.href = url;
        return;
      }
      zonaFacetas.classList.add('is-cargando');
      try {
        const respuesta = await fetch(`${url}${url.includes('?') ? '&' : '?'}section_id=${seccionId}`);
        if (!respuesta.ok) throw new Error('fetch');
        const doc = parseHTML(await respuesta.text());
        const nuevosResultados = doc.querySelector('[data-resultados]');
        const nuevaZona = doc.querySelector('[data-facetas-zona]');
        const abierto = form.classList.contains('is-abierto');

        if (nuevosResultados) document.querySelector('[data-resultados]').replaceWith(nuevosResultados);
        if (nuevaZona) {
          zonaFacetas.replaceWith(nuevaZona);
          if (abierto) {
            const nuevoForm = nuevaZona.querySelector('[data-facetas]');
            nuevoForm?.removeAttribute('inert');
            nuevoForm?.classList.add('is-abierto');
          }
        }
        if (empujarHistorial) window.history.pushState({ facetas: true }, '', url);
        document.querySelector('[data-resultados]')?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      } catch (error) {
        window.location.href = url;
      }
    };

    const urlDesdeForm = () => {
      const params = new URLSearchParams();
      for (const [clave, valor] of new FormData(form).entries()) {
        if (String(valor).trim() !== '') params.append(clave, valor);
      }
      const orden = zonaFacetas.querySelector('[data-orden]');
      if (orden && orden.value) {
        params.delete('sort_by');
        params.append('sort_by', orden.value);
      }
      const cadena = params.toString();
      return cadena ? `${form.action}?${cadena}` : form.action;
    };

    let temporizador;
    zonaFacetas.addEventListener('change', (event) => {
      if (!event.target.closest('[data-facetas]') && !event.target.matches('[data-orden]')) return;
      clearTimeout(temporizador);
      temporizador = setTimeout(() => aplicar(urlDesdeForm()), event.target.type === 'number' ? 600 : 0);
    });

    zonaFacetas.addEventListener('submit', (event) => {
      event.preventDefault();
      aplicar(urlDesdeForm());
    });

    document.addEventListener('click', (event) => {
      const enlace = event.target.closest('.facetas-activas a, [data-limpiar-facetas], .paginacion a');
      if (!enlace || !document.querySelector('[data-facetas-zona]')) return;
      event.preventDefault();
      aplicar(enlace.href);
    });

    window.addEventListener('popstate', () => aplicar(window.location.href, false));
  }


  /* ---------- Marquesina: duplicar hasta que no queden huecos ---------- */
  document.querySelectorAll('.aviso__marquesina').forEach((marquesina) => {
    const pistas = [...marquesina.querySelectorAll('.aviso__pista')];
    if (!pistas.length) return;
    const original = pistas[0];

    const rellenar = () => {
      /* Deja solo la pista original y vuelve a duplicar según el ancho real. */
      pistas.slice(1).forEach((p) => p.remove());
      marquesina.querySelectorAll('.aviso__pista--copia').forEach((p) => p.remove());

      const anchoPista = original.scrollWidth;
      if (!anchoPista) return;

      /* Tantas copias como hagan falta para cubrir dos pantallas. */
      const copias = Math.max(1, Math.ceil((window.innerWidth * 2) / anchoPista));
      for (let i = 0; i < copias; i += 1) {
        const copia = original.cloneNode(true);
        copia.classList.add('aviso__pista--copia');
        copia.setAttribute('aria-hidden', 'true');
        copia.querySelectorAll('a').forEach((a) => a.setAttribute('tabindex', '-1'));
        marquesina.appendChild(copia);
      }
      marquesina.style.setProperty('--aviso-recorrido', `${anchoPista}px`);
    };

    rellenar();
    let temporizador;
    window.addEventListener('resize', () => {
      clearTimeout(temporizador);
      temporizador = setTimeout(rellenar, 200);
    });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(rellenar);
  });

  /* ---------- Alto de la barra de aviso fija (para que la cabecera no la tape) ---------- */
  const aviso = document.querySelector('[data-aviso].aviso--fija');
  if (aviso) {
    const escritorio = window.matchMedia('(min-width: 990px)');
    const medir = () => {
      const alto = escritorio.matches ? 0 : aviso.offsetHeight;
      document.documentElement.style.setProperty('--aviso-alto', `${alto}px`);
    };
    medir();
    escritorio.addEventListener('change', medir);
    if ('ResizeObserver' in window) new ResizeObserver(medir).observe(aviso);
    else window.addEventListener('resize', medir);
  }

  /* ---------- Reseñas del carrito: rotación suave ---------- */
  const rotarResenas = (contenedor) => {
    const fichas = [...contenedor.querySelectorAll('.resena')];
    if (fichas.length < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let indice = 0;
    contenedor.__intervalo = setInterval(() => {
      fichas[indice].hidden = true;
      indice = (indice + 1) % fichas.length;
      fichas[indice].hidden = false;
    }, 6000);
  };
  document.querySelectorAll('[data-resenas]').forEach(rotarResenas);

  /* ---------- Ventana de descuento ---------- */
  const popup = document.querySelector('[data-popup]');
  if (popup) {
    const clave = popup.dataset.clave;
    const enEditor = popup.dataset.modoEditor === 'true';

    const silenciado = () => {
      try {
        const hasta = Number(window.localStorage.getItem(clave) || 0);
        return hasta > Date.now();
      } catch (error) {
        return false;
      }
    };

    const silenciar = () => {
      try {
        const dias = Number(popup.dataset.dias) || 30;
        window.localStorage.setItem(clave, String(Date.now() + dias * 86400000));
      } catch (error) {
        /* Si el navegador bloquea el almacenamiento, la ventana volverá a salir. */
      }
    };

    const cerrarPopup = (recordar = true) => {
      popup.classList.remove('is-abierto');
      if (!enEditor) popup.setAttribute('inert', '');
      document.body.classList.remove('bloqueo-scroll');
      if (recordar) silenciar();
      document.removeEventListener('keydown', popup.__onKey);
    };

    const abrirPopup = () => {
      popup.removeAttribute('inert');
      popup.classList.add('is-abierto');
      document.body.classList.add('bloqueo-scroll');
      const caja = popup.querySelector('[role="dialog"]');
      requestAnimationFrame(() => caja?.focus());
      popup.__onKey = (event) => {
        if (event.key === 'Escape') cerrarPopup();
        if (event.key !== 'Tab' || !caja) return;
        const items = [...caja.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement === caja)) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      };
      document.addEventListener('keydown', popup.__onKey);
    };

    popup.addEventListener('click', (event) => {
      if (event.target.closest('[data-popup-cerrar]')) cerrarPopup();
    });

    const yaSuscrita = popup.querySelector('.popup__exito');
    if (yaSuscrita) {
      silenciar();
      abrirPopup();
    } else if (enEditor) {
      abrirPopup();
    } else if (!silenciado()) {
      setTimeout(abrirPopup, (Number(popup.dataset.retraso) || 0) * 1000);
    }
  }

  /* ---------- Menú de escritorio: un desplegable abierto a la vez ---------- */
  const megas = [...document.querySelectorAll('[data-mega]')];
  const closeMegas = (except) => megas.forEach((d) => { if (d !== except) d.removeAttribute('open'); });
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');

  megas.forEach((details) => {
    details.addEventListener('toggle', () => { if (details.open) closeMegas(details); });
    const item = details.closest('.nav__item');
    let timer;
    item?.addEventListener('mouseenter', () => {
      if (!canHover.matches) return;
      clearTimeout(timer);
      closeMegas(details);
      details.setAttribute('open', '');
    });
    item?.addEventListener('mouseleave', () => {
      if (!canHover.matches) return;
      timer = setTimeout(() => details.removeAttribute('open'), 150);
    });
    details.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && details.open) {
        details.removeAttribute('open');
        details.querySelector('summary')?.focus();
      }
    });
  });
  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-mega]')) closeMegas();
  });

  /* ---------- Menú móvil ---------- */
  const mobileMenu = document.querySelector('[data-menu-movil]');
  if (mobileMenu) {
    const summary = mobileMenu.querySelector('summary');
    const closeMobile = () => {
      mobileMenu.removeAttribute('open');
      document.body.classList.remove('bloqueo-scroll');
      summary?.focus();
    };
    mobileMenu.addEventListener('toggle', () => {
      document.body.classList.toggle('bloqueo-scroll', mobileMenu.open);
      if (mobileMenu.open) mobileMenu.querySelector('.menu-movil__panel a, .menu-movil__panel summary')?.focus();
    });
    mobileMenu.addEventListener('click', (event) => {
      if (event.target.closest('[data-menu-movil-cerrar]')) closeMobile();
      else if (event.target === mobileMenu) closeMobile();
    });
    mobileMenu.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && mobileMenu.open) closeMobile();
    });
  }
})();


/* ---------- Cuenta atrás de envío con hora de corte real ----------
   Antes de la hora de corte (hora de España) en un día de salida:
   «Pide en las próximas 06:16:36 → Sale hoy», con el reloj en marcha.
   Pasada la hora, en fin de semana o festivo: «Pide ahora → Sale mañana /
   el lunes», sin reloj (no se inventa urgencia). Todo sale de
   Personalizar > Ficha de producto > «Aviso de envío». */
(() => {
  const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
  const ahoraMadrid = () => {
    const p = Object.fromEntries(new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Madrid', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
    }).formatToParts(new Date()).map((x) => [x.type, x.value]));
    return { y: +p.year, m: +p.month, d: +p.day, h: +p.hour % 24, min: +p.minute, s: +p.second };
  };
  const clave = (f) => `${f.getUTCFullYear()}-${String(f.getUTCMonth() + 1).padStart(2, '0')}-${String(f.getUTCDate()).padStart(2, '0')}`;
  const diaSemana = (f) => (f.getUTCDay() + 6) % 7 + 1; /* 1 = lunes … 7 = domingo */
  const dos = (n) => String(n).padStart(2, '0');

  /* Calcula si hoy aún sale (y cuánto queda) o cuándo sale el siguiente. */
  const estado = (el) => {
    const corte = Number(el.dataset.corte) || 17;
    const dias = (el.dataset.dias || '1,2,3,4,5').split(',').map((d) => Number(d.trim())).filter(Boolean);
    const festivos = new Set((el.dataset.festivos || '').split(',').map((d) => d.trim()).filter(Boolean));
    const esSalida = (f) => dias.includes(diaSemana(f)) && !festivos.has(clave(f));
    const a = ahoraMadrid();
    const hoy = new Date(Date.UTC(a.y, a.m - 1, a.d));
    if (esSalida(hoy) && a.h < corte) {
      const quedan = corte * 3600 - (a.h * 3600 + a.min * 60 + a.s);
      return { hoy: true, h: dos(Math.floor(quedan / 3600)), m: dos(Math.floor((quedan % 3600) / 60)), s: dos(quedan % 60) };
    }
    const salida = new Date(hoy);
    do { salida.setUTCDate(salida.getUTCDate() + 1); } while (!esSalida(salida));
    const manana = new Date(hoy); manana.setUTCDate(manana.getUTCDate() + 1);
    return { hoy: false, cuando: clave(salida) === clave(manana) ? 'mañana' : `el ${DIAS[salida.getUTCDay()]}` };
  };
  const mayus = (t) => t.charAt(0).toUpperCase() + t.slice(1);

  const pintar = (el) => {
    const e = estado(el);
    const etiqueta = el.querySelector('[data-aviso-envio-etiqueta]');
    const cifras = el.querySelector('[data-aviso-envio-cifras]');
    const cuando = el.querySelector('[data-aviso-envio-cuando]');
    if (e.hoy) {
      el.querySelector('[data-envio="h"]').textContent = e.h;
      el.querySelector('[data-envio="m"]').textContent = e.m;
      el.querySelector('[data-envio="s"]').textContent = e.s;
      etiqueta.textContent = 'Pide en las próximas';
      cifras.hidden = false;
      cuando.textContent = 'Hoy';
    } else {
      etiqueta.textContent = 'Pide ahora';
      cifras.hidden = true;
      cuando.textContent = mayus(e.cuando);
    }
    el.classList.toggle('aviso-envio--sin-reloj', !e.hoy);
    el.hidden = false;
  };

  /* Versión pequeña para la barra fija: «Sale hoy · 01:31:48». */
  const pintarMini = (el) => {
    const e = estado(el);
    const texto = el.querySelector('[data-envio-mini-texto]');
    const html = e.hoy ? `Sale hoy · <b>${e.h}:${e.m}:${e.s}</b>` : `Sale ${e.cuando}`;
    if (texto.innerHTML !== html) texto.innerHTML = html;
    el.hidden = false;
  };

  const todos = () => {
    document.querySelectorAll('[data-aviso-envio]').forEach(pintar);
    document.querySelectorAll('[data-envio-mini]').forEach(pintarMini);
  };
  if (!document.querySelector('[data-aviso-envio], [data-envio-mini]')) return;
  todos();
  setInterval(todos, 1000);
})();

/* ---------- Línea rotativa bajo el precio ----------
   Cambia de frase cada 3,5 s con fundido. Se para si la pestaña no se ve o
   mientras el ratón está encima. Las frases salen de Personalizar > Ficha de
   producto (o del metacampo custom.ticker): solo datos reales. */
(() => {
  document.querySelectorAll('[data-ticker]').forEach((caja) => {
    const frases = [...caja.querySelectorAll('[data-ticker-frase]')];
    if (frases.length < 2) return;
    let i = 0;
    let parado = false;
    caja.addEventListener('mouseenter', () => { parado = true; });
    caja.addEventListener('mouseleave', () => { parado = false; });
    setInterval(() => {
      if (parado || document.hidden) return;
      frases[i].classList.remove('is-activa');
      i = (i + 1) % frases.length;
      frases[i].classList.add('is-activa');
    }, 3500);
  });
})();
